import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import axios from 'axios';
import { useDialog } from '../components/DialogProvider';
import { CalendarCheck } from 'lucide-react';
import { FilterCard } from '../ui';

const API_BASE = (() => {
  const base = process.env.REACT_APP_API_URL || 'http://localhost:9000/api';
  return base.endsWith('/api') ? base : `${base}/api`;
})();

const MyBookings = () => {
  const { confirm } = useDialog();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);

  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;
  const userId = userInfo?.id || userInfo?._id;

  const token =
    userInfo?.token ||
    localStorage.getItem('token') ||
    localStorage.getItem('userToken');

  useEffect(() => {
    if (!userInfo || !userId || !token) {
      navigate('/login');
    } else {
      fetchBookings();
    }
  }, [userInfo, userId, token, navigate, selectedStatus]);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };

      const statusQuery = selectedStatus ? `?status=${selectedStatus}` : '';
      const { data } = await axios.get(
        `${API_BASE}/bookings/my-bookings${statusQuery}`,
        config
      );

      setBookings(data.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      toast.error(error.response?.data?.message || 'Error fetching bookings');
      setLoading(false);
    }
  };

  const handleCancelBooking = async (bookingId) => {
    if (!(await confirm({ title: 'Cancel booking', message: 'Are you sure you want to cancel this booking?', confirmText: 'Cancel booking', cancelText: 'Keep booking', danger: true }))) {
      return;
    }

    try {
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      };

      await axios.put(
        `${API_BASE}/bookings/${bookingId}/cancel`,
        { reason: 'Cancelled by user' },
        config
      );

      toast.success('Booking cancelled successfully');
      fetchBookings();
    } catch (error) {
      console.error('Error cancelling booking:', error);
      toast.error(error.response?.data?.message || 'Error cancelling booking');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Confirmed':
        return 'bg-green-100 text-green-800';
      case 'Pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'Checked-In':
        return 'bg-blue-100 text-blue-800';
      case 'Completed':
        return 'bg-gray-100 text-gray-800';
      case 'Cancelled':
        return 'bg-red-100 text-red-800';
      case 'No-Show':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="min-h-screen bg-cm-bg text-cm-text pb-20">
      {/* Header */}
      <div className="bg-cm-surface text-cm-text border-b border-cm-border px-4 h-14 flex items-center sticky top-0 z-10">
        <div className="flex items-center justify-between w-full max-w-2xl mx-auto">
          <div className="flex items-center">
            <button onClick={() => navigate('/dashboard')} className="mr-3 w-9 h-9 grid place-items-center rounded-cm bg-cm-surface-2" aria-label="Back">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h1 className="font-display text-lg font-extrabold m-0">My Bookings</h1>
          </div>
          <button
            onClick={() => navigate('/book-seat')}
            className="bg-cm-gold text-cm-gold-ink px-4 h-9 rounded-cm text-sm font-semibold hover:bg-cm-gold-hover transition-colors"
          >
            + New Booking
          </button>
        </div>
      </div>

      <div className="p-4 max-w-2xl mx-auto">
        {/* Filter */}
        <FilterCard
          id="booking-filters"
          className="mb-4"
          inlineToggle
          open={filtersOpen}
          onOpenChange={setFiltersOpen}
          chips={selectedStatus ? [{ icon: CalendarCheck, label: selectedStatus }] : []}
          emptyLabel="All bookings"
        >
          <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Status</label>
          <select
            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cm-gold"
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
          >
            <option value="">All Bookings</option>
            <option value="Pending">Pending</option>
            <option value="Confirmed">Confirmed</option>
            <option value="Checked-In">Checked-In</option>
            <option value="Completed">Completed</option>
            <option value="Cancelled">Cancelled</option>
          </select>
          </div>
        </FilterCard>

        {/* Bookings List */}
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin h-8 w-8 border-4 border-cm-gold border-t-transparent rounded-full mx-auto mb-4"></div>
            <p>Loading bookings...</p>
          </div>
        ) : bookings.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <div className="text-6xl mb-4">📅</div>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No bookings found</h3>
            <p className="text-gray-500 mb-4">
              You haven't made any bookings yet. Start by booking a seat!
            </p>
            <button
              onClick={() => navigate('/book-seat')}
              className="bg-cm-gold text-cm-gold-ink px-6 py-3 rounded-lg font-semibold hover:bg-cm-gold-hover transition-colors"
            >
              Book a Seat
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {bookings.map((booking) => (
              <div key={booking._id} className="bg-white rounded-lg shadow-md p-4">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h3 className="font-semibold text-lg">{booking.club?.name}</h3>
                    <p className="text-sm text-gray-600">
                      {booking.table?.tableName} - Seat {booking.seatNumber}
                    </p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(booking.status)}`}>
                    {booking.status}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-3 mb-3 text-sm">
                  <div>
                    <p className="text-gray-500">Booking Number</p>
                    <p className="font-semibold">{booking.bookingNumber}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Game Type</p>
                    <p className="font-semibold">{booking.table?.gameType || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Date</p>
                    <p className="font-semibold">{formatDate(booking.bookingDate)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Time</p>
                    <p className="font-semibold">{booking.bookingTime}</p>
                  </div>
                </div>

                {booking.notes && (
                  <div className="bg-gray-50 rounded p-2 mb-3 text-sm">
                    <p className="text-gray-500 text-xs">Notes:</p>
                    <p className="text-gray-700">{booking.notes}</p>
                  </div>
                )}

                <div className="flex justify-between items-center pt-3 border-t">
                  <p className="text-xs text-gray-500">
                    Booked on {new Date(booking.createdAt).toLocaleDateString()}
                  </p>
                  {(booking.status === 'Pending' || booking.status === 'Confirmed') && (
                    <button
                      onClick={() => handleCancelBooking(booking._id)}
                      className="text-red-600 text-sm font-semibold hover:text-red-700"
                    >
                      Cancel Booking
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyBookings;
